import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Button,
  Paper,
  Chip,
  IconButton,
  Divider,
  AppBar,
  Toolbar,
  Autocomplete,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Close as CloseIcon,
  CameraAlt as CameraAltIcon,
} from '@mui/icons-material';
// Using simple HTML date inputs instead of MUI X date picker for compatibility
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store/rootReducers';

// Import existing components
import MediaUpload from '../../Components/MediaUpload';
import CategoryChips from '../../Components/CategoryChips';
import DeleteIcon from '@mui/icons-material/Delete';
import MapSelector from '../../Components/MapSelector';
import AutoCompletePlaces from '../../Components/AutoCompletePlaces';
import { request } from '../../Utils/request';
import { fetchCategories } from '../../services/CommonApiCall';
import { getSignedUploadUrls, uploadFileToSignedUrl } from '../../services/API/Api';
import { tripDraftUtils } from '../../Utils/tripDraftStorage';
import { fileToBase64, base64ToFile } from '../../Utils/base64Converter';
import { rupeesToPaise } from '../../Utils/currencyConverter';
import { ReactComponent as DraftIcon } from "../../Icons/svg/draft.svg"; 

interface TaskFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
}

interface DailyTask {
  placeRefId?: string;
  name?: string;
  file?: TaskFile;
  description: string;
}

interface DailySchedule {
  day: string;
  tasks: DailyTask[];
}

interface Schedule {
  dailySchedule: DailySchedule[];
}

interface Batch {
  from: string;
  to: string;
  groupSize: number;
  maxGroupSizeAllowed: number;
}

interface TripFile {
  fileKey: string;
  fileType: string;
  featured: boolean;
}

interface Location {
  lat: number;
  lng: number;
  name: string;
}

interface TripFormData {
  type: 'Trip' | 'Itinerary';
  title: string;
  description: string;
  schedule: Schedule;
  pricePerPerson: number;
  batches: Batch[];
  files: TripFile[];
  notes: string;
  category: string[];
  location: Location | null;
}

interface PreviewImage {
  url: string;
  caption: string;
  featured: boolean;
  file?: File;
}

interface PreviewVideo {
  url: string;
  file?: File;
}

const CreateTrip: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const categories = useSelector((state: RootState) => state.category?.categories || []);
  const tripDrafts = useSelector((state: RootState) => state.tripDraft?.drafts || []);
  const user = useSelector((state: RootState) => state.user?.user);
  
  useEffect(() => {
    fetchCategories(dispatch,'trip-itinerary');
  }, []);

  // Handle draft loading from navigation state
  useEffect(() => {
    if (location.state?.draft) {
      const draft = location.state.draft;
      const isEdit = location.state.isEdit;
      const isPreview = location.state.isPreview;
      
      if (draft) {
        setFormData({
          type: draft.type,
          title: draft.title,
          description: draft.description,
          schedule: draft.schedule,
          pricePerPerson: draft.pricePerPerson,
          batches: draft.batches,
          files: draft.files,
          notes: draft.notes,
          category: draft.category,
          location: draft.location,
        });
        
        // Update the type state to match the draft type
        setType(draft.type);
        setSelectedCategories(draft.category);
        setCurrentDraftId(draft.id);
        
        // Convert base64 images back to preview format
        if (draft.imagesData && draft.imagesData.length > 0) {
          const restoredImages = draft.imagesData.map((img: any) => {
            const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
            const restoredImage = {
              url: newFile ? URL.createObjectURL(newFile) : img.url,
              caption: img.caption || '',
              featured: img.featured || false,
              file: newFile || undefined,
            };
            return restoredImage;
          });
          setImagesData(restoredImages);
          // Force MediaUpload component to re-render with new data
          setMediaUploadKey(prev => prev + 1);
        }
        
      }
    }
  }, [location.state]);

  const [formData, setFormData] = useState<TripFormData>({
    type: 'Trip',
    title: '',
    description: '',
    schedule: {
      dailySchedule: [
        {
          day: '1',
          tasks: [
            {
              name: '',
              description: '',
              placeRefId: ''
            }
          ]
        }
      ]
    },
    pricePerPerson: 0,
    batches: [
      {
        from: '',
        to: '',
        groupSize: 0,
        maxGroupSizeAllowed: 1
      }
    ],
    files: [],
    notes: '',
    category: [],
    location: null,
  });

  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [type, setType] = useState<string>('Trip');
  // Local state to keep raw image previews (with File objects)
  const [imagesData, setImagesData] = useState<PreviewImage[]>([]);
  
  // Draft-related state
  const [currentDraftId, setCurrentDraftId] = useState<string | null>(null);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [mediaUploadKey, setMediaUploadKey] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Track which fields have been touched and if submit was attempted
  const [touchedFields, setTouchedFields] = useState<{[key: string]: boolean}>({});
  const [submitAttempted, setSubmitAttempted] = useState(false);

  // Track which day is currently selected in the Daily Schedule view
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
  
  // Places data for autocomplete
  const [places, setPlaces] = useState<any[]>([]);
  const [loadingPlaces, setLoadingPlaces] = useState(false);

  const availableCategories = categories;

  // Get tomorrow's date in YYYY-MM-DD format for date validation
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };
  const minDate = getTomorrowDate();

  // Helper function to mark a field as touched
  const markFieldTouched = (fieldName: string) => {
    setTouchedFields(prev => ({ ...prev, [fieldName]: true }));
  };

  // Helper function to check if we should show error for a field
  const shouldShowError = (fieldName: string) => {
    return touchedFields[fieldName] || submitAttempted;
  };

  // Fetch places from API
  useEffect(() => {
    const fetchPlaces = async () => {
      setLoadingPlaces(true);
      try {
        const response = await request<any>('/places', {
          method: 'GET',
          authorization: true,
        });
        
        if (response?.data) {
          // Format places for autocomplete
          const formattedPlaces = response.data.map((place: any) => ({
            label: place.name || place.title,
            placeRefId: place._id,
            ...place
          }));
          setPlaces(formattedPlaces);
        }
      } catch (error) {
        console.error('Error fetching places:', error);
      } finally {
        setLoadingPlaces(false);
      }
    };

    fetchPlaces();
  }, []);

  const handleInputChange = (field: keyof TripFormData, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Capture image previews (with File objects) but defer uploading until submit
  const handleFilesChange = (images: PreviewImage[]) => {
    setImagesData(images);
  };

  const handleLocationSelect = (location: { lat: number; lng: number; address: string }) => {
    setFormData(prev => ({
      ...prev,
      location: {
        lat: location.lat,
        lng: location.lng,
        name: location.address
      }
    }));
  };

  const handleCategoriesChange = (categories: any[]) => {
    const categoryNames = categories.map(cat => cat.name);
    setFormData(prev => ({
      ...prev,
      category: categoryNames
    }));
    setSelectedCategories(categoryNames);
  };

  const addDay = () => {
    const newDayNumber = formData.schedule.dailySchedule.length + 1;
    const newDay: DailySchedule = {
      day: `${newDayNumber}`,
      tasks: [
        {
          name: '',
          description: '',
          placeRefId: ''
        }
      ]
    };
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: [...prev.schedule.dailySchedule, newDay]
      }
    }));

    // Automatically focus the newly added day
    setSelectedDayIndex(newDayNumber - 1);
  };

  const removeDay = (dayIndex: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule
          .filter((_, index) => index !== dayIndex)
          .map((day, index) => ({
            ...day,
            day: `${index + 1}`
          }))
      }
    }));

    setSelectedDayIndex(prev => {
      if (prev === dayIndex) return 0; // reset to first day if the current one is removed
      if (prev > dayIndex) return prev - 1; // shift left when an earlier day is removed
      return prev; // no change needed
    });
  };

  const addTask = (dayIndex: number) => {
    const newTask: DailyTask = {
      name: '',
      description: '',
      placeRefId: ''
    };
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
          index === dayIndex
            ? { ...day, tasks: [...day.tasks, newTask] }
            : day
        )
      }
    }));
  };

  const removeTask = (dayIndex: number, taskIndex: number) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
          index === dayIndex
            ? { ...day, tasks: day.tasks.filter((_, tIndex) => tIndex !== taskIndex) }
            : day
        )
      }
    }));
  };

  const updateDay = (dayIndex: number, field: keyof DailyTask, value: any, taskIndex: number = 0) => {
    setFormData(prev => ({
      ...prev,
      schedule: {
        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
          index === dayIndex
            ? {
              ...day,
              tasks: day.tasks.map((task, tIndex) =>
                tIndex === taskIndex ? { ...task, [field]: value } : task
              )
            }
            : day
        )
      }
    }));
  };

  const updateBatch = (index: number, field: keyof Batch, value: any) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.map((batch, i) =>
        i === index ? { ...batch, [field]: value } : batch
      )
    }));
  };

  const addBatch = () => {
    const newBatch: Batch = {
      from: '',
      to: '',
      groupSize: 0,
      maxGroupSizeAllowed: 0
    };
    setFormData(prev => ({
      ...prev,
      batches: [...prev.batches, newBatch]
    }));
  };

  const removeBatch = (index: number) => {
    setFormData(prev => ({
      ...prev,
      batches: prev.batches.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions
    
    // Mark that submit was attempted (to show all errors)
    setSubmitAttempted(true);
    setIsSubmitting(true);
    
    try {
      // VALIDATION: Check all mandatory fields before proceeding
      const validationErrors: string[] = [];
      
      // 1. Title validation
      if (!formData.title.trim()) {
        validationErrors.push('Title is required');
      }
      
      // 2. Description validation
      if (!formData.description.trim()) {
        validationErrors.push('Description is required');
      }
      
      // 3. Category validation
      if (!formData.category || formData.category.length === 0) {
        validationErrors.push('Please select at least one category');
      }
      
      // 4. Location validation
      if (!formData.location || !formData.location.name) {
        validationErrors.push('Location is required');
      }
      
      // 5. Price validation
      if (!formData.pricePerPerson || formData.pricePerPerson <= 0) {
        validationErrors.push('Price per person must be greater than 0');
      }
      
      // 6. Schedule validation
      if (!formData.schedule.dailySchedule || formData.schedule.dailySchedule.length === 0) {
        validationErrors.push('Please add at least one day to the schedule');
      } else {
        // Check each day has at least one task with description
        formData.schedule.dailySchedule.forEach((day, dayIndex) => {
          if (!day.tasks || day.tasks.length === 0) {
            validationErrors.push(`Day ${dayIndex + 1} must have at least one task`);
          } else {
            day.tasks.forEach((task, taskIndex) => {
              if (!task.description || !task.description.trim()) {
                validationErrors.push(`Day ${dayIndex + 1}, Task ${taskIndex + 1} description is required`);
              }
              if (!task.name && !task.placeRefId) {
                validationErrors.push(`Day ${dayIndex + 1}, Task ${taskIndex + 1} must have either a name or place selected`);
              }
            });
          }
        });
      }
      
      // 7. Batch validation (only for Trips)
      if (type === 'Trip') {
        if (!formData.batches || formData.batches.length === 0) {
          validationErrors.push('Please add at least one batch for the trip');
        } else {
          const today = new Date();
          today.setHours(0, 0, 0, 0); // Set to start of today
          
          formData.batches.forEach((batch, batchIndex) => {
            // Check if from date is provided
            if (!batch.from) {
              validationErrors.push(`Batch ${batchIndex + 1}: Start date is required`);
            } else {
              const fromDate = new Date(batch.from);
              fromDate.setHours(0, 0, 0, 0);
              // Check if from date is today or before
              if (fromDate <= today) {
                validationErrors.push(`Batch ${batchIndex + 1}: Start date must be after today`);
              }
            }
            
            // Check if to date is provided
            if (!batch.to) {
              validationErrors.push(`Batch ${batchIndex + 1}: End date is required`);
            } else if (batch.from) {
              const fromDate = new Date(batch.from);
              const toDate = new Date(batch.to);
              toDate.setHours(0, 0, 0, 0);
              
              // Check if to date is today or before
              if (toDate <= today) {
                validationErrors.push(`Batch ${batchIndex + 1}: End date must be after today`);
              }
              
              // Check if to date is after from date
              if (toDate <= fromDate) {
                validationErrors.push(`Batch ${batchIndex + 1}: End date must be after start date`);
              }
            }
            
            // Check max group size
            if (!batch.maxGroupSizeAllowed || batch.maxGroupSizeAllowed <= 0) {
              validationErrors.push(`Batch ${batchIndex + 1}: Max group size is required and must be greater than 0`);
            }
          });
        }
      }
      
      // 8. Image validation
      const mediaFiles = imagesData;
      if (!mediaFiles || mediaFiles.length === 0) {
        validationErrors.push('Please upload at least one image');
      }
      
      // If there are validation errors, show them and stop
      if (validationErrors.length > 0) {
        const errorMessage = 'Please fix the following errors:\n\n' + validationErrors.map((err, idx) => `${idx + 1}. ${err}`).join('\n');
        alert(errorMessage);
        setIsSubmitting(false);
        return;
      }

      // Continue with existing image validation
      if (!mediaFiles || mediaFiles.length === 0) {
        alert('Please upload at least one image.');
        setIsSubmitting(false);
        return;
      }

      // Validate files before processing
      const validMediaFiles = mediaFiles.filter((item: any) => {
        if (!item.file) {
          console.error('File object missing for item:', item);
          return false;
        }
        if (!item.file.type.startsWith('image/')) {
          console.error('Invalid file type:', item.file.type);
          return false;
        }
        if (item.file.size > 10 * 1024 * 1024) { // 10MB limit
          console.error('File too large:', item.file.name, item.file.size);
          return false;
        }
        return true;
      });

      if (validMediaFiles.length === 0) {
        alert('Please upload valid image files (max 10MB each).');
        return;
      }

      // Prepare file upload request payload
      const filesProp = validMediaFiles.map((item: any) => ({
        folder: formData.type === 'Trip' ? 'trip' : 'itinerary',
        fileType: String(item.file.type),
      }));

      const signedUrlsData = await getSignedUploadUrls(filesProp);
      const signedFiles = signedUrlsData?.data?.files || [];

      if (signedFiles.length !== mediaFiles.length) {
        alert('Could not obtain signed URLs for all files.');
        return;
      }

      const uploadedFiles = await Promise.all(validMediaFiles.map(async (item: any, index: number) => {
        try {
          const { uploadUrl, fileKey } = signedFiles[index];
          if (!uploadUrl || !fileKey) {
            console.error('Missing upload URL or file key for index:', index);
            return null;
          }

          if (!item.file) {
            console.error('File object missing for item at index:', index);
            return null;
          }

          // Perform the upload with timeout
          const uploadResponse = await Promise.race([
            uploadFileToSignedUrl(item.file, uploadUrl),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Upload timeout')), 30000)
            )
          ]);

          if (uploadResponse.status === 200) {
            return {
              fileKey,
              fileType: String(item.file.type),
              featured: index === 0,
            } as TripFile;
          } else {
            console.error('Upload failed with status:', uploadResponse.status);
            return null;
          }
        } catch (error) {
          console.error('Upload error for file:', item.file?.name, error);
          return null;
        }
      }));

      const validFiles: TripFile[] = uploadedFiles.filter(Boolean) as TripFile[];
      const failedUploads = uploadedFiles.filter(file => file === null).length;

      if (validFiles.length === 0) {
        alert('File upload failed. Please check your internet connection and try again.');
        return;
      }

      if (failedUploads > 0) {
        console.warn(`${failedUploads} files failed to upload, but ${validFiles.length} files uploaded successfully.`);
      }

      /**
       * Sanitize schedule tasks - mutually exclusive keys:
       * If placeRefId exists → Remove name completely
       * If name exists → Remove placeRefId completely
       * Never send both keys together
       */
      const sanitizedSchedule: Schedule = {
        dailySchedule: formData.schedule.dailySchedule.map(day => ({
          ...day,
          tasks: day.tasks.map(task => {
            const { name, description, placeRefId, file } = task as any;
            const sanitizedTask: any = { description };
            
            // Add file if present
            if (file) sanitizedTask.file = file;
            
            // If placeRefId exists, ONLY include placeRefId (remove name)
            if (placeRefId && placeRefId.trim().length > 0) {
              sanitizedTask.placeRefId = placeRefId;
              // Don't add name property at all
            } 
            // If no placeRefId but name exists, ONLY include name (remove placeRefId)
            else if (name && name.trim().length > 0) {
              sanitizedTask.name = name;
              // Don't add placeRefId property at all
            }
            // If neither, at least have description
            
            return sanitizedTask;
          }),
        })),
      };

      const body: TripFormData = {
        ...formData,
        schedule: sanitizedSchedule,
        files: validFiles,
        pricePerPerson: rupeesToPaise(formData.pricePerPerson), // Convert rupees to paise
      };

      // Determine the endpoint based on the type
      const endpoint = type === 'Trip' ? '/trips' : '/itineraries';
      
      const response = await request<any, TripFormData>(endpoint, {
        method: 'POST',
        body,
        authorization: true,
      });

      console.log(`${formData.type} created:`, response);
      alert(`${type} created successfully`);

      /**
       * STACK CLEAR REQUIRED HERE:
       * After successful trip/itinerary creation, we replace the creation form page in history to:
       * 1. Prevent users from going back to the trip creation form after successful submission
       * 2. Avoid accidental duplicate trip/itinerary creation if user navigates back
       * 3. Navigate directly to the created trip detail page for immediate viewing
       * 4. Clear potentially large media/file data and trip details from form history
       * 5. Follow standard content creation UX patterns for trip management
       */
      // Navigate to the newly created trip's detail page
      const tripId = response?.data?._id || response?._id;
      if (tripId) {
        navigate(`/trips/${tripId}?type=${type.toLowerCase()}`, { 
          replace: true,
          state: { type: type.toLowerCase() }
        });
      } else {
        navigate('/trips', { replace: true });
      }
    } catch (error) {
      console.error('Error creating trip:', error);
      
      // Provide more specific error messages
      if (error instanceof Error) {
        if (error.message.includes('timeout')) {
          alert('Upload timed out. Please check your internet connection and try again.');
        } else if (error.message.includes('File object missing')) {
          alert('Some image files are missing. Please re-select your images and try again.');
        } else if (error.message.includes('Invalid file type')) {
          alert('Please upload only image files (JPG, PNG, etc.).');
        } else if (error.message.includes('File too large')) {
          alert('Some files are too large. Please use images smaller than 10MB each.');
        } else {
          alert(`Trip creation failed: ${error.message}`);
        }
      } else {
        alert('Trip creation failed. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Draft handling functions
  const handleSaveDraft = async () => {
    try {
      setIsSavingDraft(true);
      
      // Convert File objects to base64 when saving
      const serializedImagesData = await Promise.all(imagesData.map(async img => ({
        url: img.url,
        caption: img.caption || '',
        featured: img.featured || false,
        fileBase64: img.file ? await fileToBase64(img.file) : null,
        fileName: img.file?.name || '',
        fileType: img.file?.type || '',
        fileSize: img.file?.size || 0
      })));

      const draftData = {
        type: formData.type,
        title: formData.title,
        description: formData.description,
        schedule: formData.schedule,
        pricePerPerson: formData.pricePerPerson,
        batches: formData.batches,
        files: formData.files,
        notes: formData.notes,
        category: formData.category,
        location: formData.location,
        imagesData: serializedImagesData,
        videosData: [], // No videos in current implementation
        author: {
          _id: user?._id || '',
          username: user?.username || '',
        },
      };

      if (currentDraftId) {
        tripDraftUtils.updateDraft(dispatch, currentDraftId, draftData as any);
        alert('Draft updated successfully!');
      } else {
        tripDraftUtils.saveDraft(dispatch, draftData as any);
        setCurrentDraftId(Date.now().toString());
        alert('Draft saved successfully!');
      }
    } catch (error) {
      console.error('Error saving draft:', error);
      alert('Failed to save draft. Please try again.');
    } finally {
      setIsSavingDraft(false);
    }
  };

  const handleLoadDraft = (draftId: string) => {
    const draft = tripDrafts.find(d => d.id === draftId);
    if (draft) {
      setFormData({
        type: draft.type,
        title: draft.title,
        description: draft.description,
        schedule: draft.schedule,
        pricePerPerson: draft.pricePerPerson,
        batches: draft.batches,
        files: draft.files,
        notes: draft.notes,
        category: draft.category,
        location: draft.location,
      });
      
      // Convert base64 images back to preview format
      console.log('handleLoadDraft - Loading images:', draft.imagesData);
      const restoredImages = draft.imagesData.map((img: any) => {
        const newFile = img.fileBase64 ? base64ToFile(img.fileBase64, img.fileName, img.fileType) : null;
        const restoredImage = {
          url: newFile ? URL.createObjectURL(newFile) : img.url,
          caption: img.caption || '',
          featured: img.featured || false,
          file: newFile || undefined,
        };
        console.log('handleLoadDraft - Restored image:', restoredImage);
        return restoredImage;
      });
      setImagesData(restoredImages);
      // Force MediaUpload component to re-render with new data
      setMediaUploadKey(prev => prev + 1);
      console.log('handleLoadDraft - Set imagesData and updated mediaUploadKey');
      
      setCurrentDraftId(draftId);
      setSelectedCategories(draft.category);
    }
  };

  return (
     

      <Container sx={{ py: 3 , backgroundColor: 'white'}}>
        <Box sx={{ borderRadius: 2 }}>
          
          {/* Header with Draft Buttons */}
          <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ color: 'black' }}>
                Create {formData.type}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} 
                onClick={() => navigate('/trip/drafts')}>
                <DraftIcon />
                <Typography fontWeight={"medium"} sx={{ color: '#008387', textTransform: 'none' }}>{'Open from Draft'}</Typography>
                </Box>
              </Box>
            </Box>
          </Box>

          {/* Media Upload Section */}
          <Box sx={{ mb: 3 }}>
            <MediaUpload
              key={mediaUploadKey}
              onImagesChange={handleFilesChange}
              mediaMode="image-only"
              maxImages={5}
              value={imagesData}
              type={'image'}
            />
          </Box>

          {/* Trip Type Toggle */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Create
            </Typography>
            <ToggleButtonGroup
              value={formData.type}
              exclusive
              onChange={(_, value) => value && handleInputChange('type', value)}
              sx={{
                mb: 2,
                '& .MuiToggleButton-root': {
                  borderRadius: '20px !important',
                  px: 3,
                  border: '1px solid #FF6B35',
                  color: 'text.primary',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 107, 53, 0.1)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: '#FF6B35 !important',
                    color: 'white !important',
                    '&:hover': {
                      backgroundColor: '#E55A2B !important',
                    }
                  }
                }
              }}
            >
              <ToggleButton onClick={() => {setType('Trip')}} value="Trip" sx={{ mx: 1 }}>
                Trip
              </ToggleButton>
              <ToggleButton onClick={() => {setType('Itinerary')}} value="Itinerary" sx={{ mx: 1 }}>
                Itinerary
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Basic Information */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={type === 'Trip' ? "Trip title" : "Itinerary title"}
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              onBlur={() => markFieldTouched('title')}
              sx={{ mb: 1 }}
              required
              error={shouldShowError('title') && !formData.title.trim()}
              helperText={shouldShowError('title') && !formData.title.trim() ? 'Title is required' : ''}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label={ "Description"}
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              onBlur={() => markFieldTouched('description')}
              sx={{ mb: 1 }}
              required
              error={shouldShowError('description') && !formData.description.trim()}
              helperText={shouldShowError('description') && !formData.description.trim() ? 'Description is required' : ''}
            />

            <TextField
              fullWidth
              multiline
              rows={3}
              label={type === 'Trip' ? "Trip notes (optional)" : "Itinerary notes (optional)"}
              value={formData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              sx={{ mb: 1 }}
            />
          </Box>

          {/* Categories */}
          <Box sx={{ mb: 3 }} onClick={() => markFieldTouched('category')}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Categories *
            </Typography>
            <CategoryChips
              categories={availableCategories}
              selectedCategories={selectedCategories}
              onChange={(cats) => {
                handleCategoriesChange(cats);
                markFieldTouched('category');
              }}
            />
            {shouldShowError('category') && (!formData.category || formData.category.length === 0) && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                Please select at least one category
              </Typography>
            )}
          </Box>

          {/* Location */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'text.secondary' }}>
              Location *
            </Typography>
            <AutoCompletePlaces
              key={formData.location?.lat}
              value={{
                name: formData.location?.name,
                lat: formData.location?.lat || 0,
                lng: formData.location?.lng || 0,
              }}
              onPlaceSelect={(place: any) => {
                console.log("place", place);
                markFieldTouched('location');
                handleLocationSelect({
                  lat: place.lat,
                  lng: place.lng,
                  address: place.name,
                });
              }}
              label="Location"
              placeholder="Enter a location"
            />
            {shouldShowError('location') && (!formData.location || !formData.location.name) && (
              <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                Location is required
              </Typography>
            )}
          </Box>

          {/* Price - Only show for Trips */}
          {/* {type === 'Trip' && ( */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label={type === 'Trip' ? "Price per person" : "Budget for itinerary"}
              value={formData.pricePerPerson === 0 ? '' : formData.pricePerPerson}
              onChange={(e) => {
                const { value } = e.target;
                if (value === '') {
                  handleInputChange('pricePerPerson', 0);
                  return;
                }

                if (/^\d+$/.test(value)) {
                  handleInputChange('pricePerPerson', Number(value));
                }
              }}
              onBlur={() => markFieldTouched('pricePerPerson')}
              sx={{ mb: 2 }}
              required
              error={shouldShowError('pricePerPerson') && (!formData.pricePerPerson || formData.pricePerPerson <= 0)}
              helperText={shouldShowError('pricePerPerson') && (!formData.pricePerPerson || formData.pricePerPerson <= 0) ? 'Price must be greater than 0' : ''}
              inputProps={{ min: 1 }}
            />
          </Box>
          {/* )} */}

          {/* Batch Information */}
            {type === 'Trip' && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Create batches
            </Typography>
            {formData.batches.map((batch, batchIndex) => (
              <Box key={batchIndex} sx={{ mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
               
                  <Typography sx={{ mb: 2, color: 'text.primary', fontSize: '16px' }}>{`Batch ${batchIndex + 1}`}</Typography>
              
                  {formData.batches.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeBatch(batchIndex)}
                      sx={{ ml: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
                <Box >
                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', alignItems: 'center' }}>
                    <TextField
                      fullWidth
                      type="date"
                      label="Start Date"
                      value={batch.from.split('T')[0] || ''}
                      onChange={(e) => updateBatch(batchIndex, 'from', `${e.target.value}T00:00:00.000Z`)}
                      onBlur={() => markFieldTouched(`batch_${batchIndex}_from`)}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: minDate }}
                      required
                      error={shouldShowError(`batch_${batchIndex}_from`) && (!batch.from || new Date(batch.from).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0))}
                      helperText={shouldShowError(`batch_${batchIndex}_from`) && (!batch.from || new Date(batch.from).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0)) ? 'Start date must be after today' : ''}
                    />
                    <TextField
                      fullWidth
                      type="date"
                      label="End Date"
                      value={batch.to.split('T')[0] || ''}
                      onChange={(e) => updateBatch(batchIndex, 'to', `${e.target.value}T00:00:00.000Z`)}
                      onBlur={() => markFieldTouched(`batch_${batchIndex}_to`)}
                      sx={{ mb: 2 }}
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ min: minDate }}
                      required
                      error={shouldShowError(`batch_${batchIndex}_to`) && (!batch.to || new Date(batch.to).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0))}
                      helperText={shouldShowError(`batch_${batchIndex}_to`) && (!batch.to || new Date(batch.to).setHours(0,0,0,0) <= new Date().setHours(0,0,0,0)) ? 'End date must be after today' : ''}
                    />
                  </Box>

                  <Box sx={{ display: 'flex', gap: 2, flexDirection: 'row', alignItems: 'center' }}>

                    <TextField
                      fullWidth
                      type="number"
                      label="Max Group Size Allowed"
                      value={batch.maxGroupSizeAllowed || ''}
                      onChange={(e) => updateBatch(batchIndex, 'maxGroupSizeAllowed', Number(e.target.value))}
                      onBlur={() => markFieldTouched(`batch_${batchIndex}_maxGroupSize`)}
                      sx={{ mt: 2 }}
                      required
                      error={shouldShowError(`batch_${batchIndex}_maxGroupSize`) && (!batch.maxGroupSizeAllowed || batch.maxGroupSizeAllowed <= 0)}
                      helperText={shouldShowError(`batch_${batchIndex}_maxGroupSize`) && (!batch.maxGroupSizeAllowed || batch.maxGroupSizeAllowed <= 0) ? 'Max group size is required and must be greater than 0' : ''}
                      inputProps={{ min: 1 }}
                    />
                  </Box>
                </Box>
                {batchIndex < formData.batches.length - 1 && <Divider sx={{ mt: 2 }} />}
              </Box>
            ))}

            <Button
              variant="text"
              sx={{ color: '#FF6B35', textTransform: 'none' }}
              onClick={addBatch}
              startIcon={<AddIcon />}
            >
              Add more batches
            </Button>
          </Box>
          )}

          {/* Daily Schedule */}
          <Box sx={{ mb: 3, backgroundColor:'#fff8f5',borderTopLeftRadius:'24px',borderTopRightRadius:'24px',p:2,  border: '2px solid #eedcce', }}>
            {/* <Typography variant="subtitle2" sx={{ mb: 2, color: 'text.secondary' }}>
              Daily Schedule
            </Typography> */}

            {/* Day Tabs */}
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {formData.schedule.dailySchedule.map((day, index) => (
                <Chip
                  key={index}
                  label={`Day ${day.day}`}
                  clickable
                  onClick={() => setSelectedDayIndex(index)}
                  sx={{
                    backgroundColor: selectedDayIndex === index ? '#FF6B35 !important' : '#E0E0E0',
                    color: selectedDayIndex === index ? 'white !important' : 'text.primary',
                    fontWeight: 'bold'
                  }}
                />
              ))}

              {/* Add / Remove Day Buttons */}
              <IconButton size="small" onClick={addDay}>
                <AddIcon />
              </IconButton>
              {formData.schedule.dailySchedule.length > 1 && (
                <IconButton size="small" onClick={() => removeDay(selectedDayIndex)}>
                  <CloseIcon />
                </IconButton>
              )}
            </Box>

            {/* Tasks for selected day */}
            {formData.schedule.dailySchedule[selectedDayIndex]?.tasks.map((task, taskIndex) => (
              <Box key={taskIndex} sx={{ mb: 2, pl: 2, borderLeft: '3px solid #FF6B35' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {formData.schedule.dailySchedule[selectedDayIndex].tasks.length > 1 && (
                    <IconButton
                      size="small"
                      onClick={() => removeTask(selectedDayIndex, taskIndex)}
                      sx={{ ml: 'auto' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
                <Autocomplete
                  disablePortal
                  sx={{ mb: 2 }}
                  options={places}
                  getOptionLabel={(option) => option.label || ''}
                  loading={loadingPlaces}
                  value={places.find((p) => p.placeRefId === task.placeRefId) || null}
                  renderInput={(params) => <TextField {...params} label="Select Place (optional)" />}
                  onChange={(event, value) => {
                    console.log('Selected place:', value);
                    markFieldTouched(`task_${selectedDayIndex}_${taskIndex}_name`);
                    if (value) {
                      // Place selected: Set placeRefId and remove name completely
                      setFormData(prev => ({
                        ...prev,
                        schedule: {
                          dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
                            index === selectedDayIndex
                              ? {
                                ...day,
                                tasks: day.tasks.map((t, tIndex) => {
                                  if (tIndex === taskIndex) {
                                    const { name, ...taskWithoutName } = t;
                                    return { ...taskWithoutName, placeRefId: value.placeRefId } as DailyTask;
                                  }
                                  return t;
                                })
                              }
                              : day
                          )
                        }
                      }));
                    } else {
                      // Place cleared: Remove placeRefId
                      setFormData(prev => ({
                        ...prev,
                        schedule: {
                          dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
                            index === selectedDayIndex
                              ? {
                                ...day,
                                tasks: day.tasks.map((t, tIndex) => {
                                  if (tIndex === taskIndex) {
                                    const { placeRefId, ...taskWithoutPlace } = t;
                                    return taskWithoutPlace;
                                  }
                                  return t;
                                })
                              }
                              : day
                          )
                        }
                      }));
                    }
                  }}
                />

                <TextField
                  fullWidth
                  label="Title"
                  value={task.name || ''}
                  onChange={(e) => {
                    // Custom name entered: Set name and remove placeRefId completely
                    const customName = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      schedule: {
                        dailySchedule: prev.schedule.dailySchedule.map((day, index) =>
                          index === selectedDayIndex
                            ? {
                              ...day,
                              tasks: day.tasks.map((t, tIndex) => {
                                if (tIndex === taskIndex) {
                                  const { placeRefId, ...taskWithoutPlace } = t;
                                  return { ...taskWithoutPlace, name: customName } as DailyTask;
                                }
                                return t;
                              })
                            }
                            : day
                        )
                      }
                    }));
                  }}
                  onBlur={() => markFieldTouched(`task_${selectedDayIndex}_${taskIndex}_name`)}
                  sx={{ mb: 2 }}
                  error={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_name`) && !task.name && !task.placeRefId}
                  helperText={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_name`) && !task.name && !task.placeRefId ? 'Task name or place is required' : ''}
                />

                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Task description"
                  value={task.description || ''}
                  onChange={(e) => updateDay(selectedDayIndex, 'description', e.target.value, taskIndex)}
                  onBlur={() => markFieldTouched(`task_${selectedDayIndex}_${taskIndex}_description`)}
                  sx={{ mb: 2 }}
                  required
                  error={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_description`) && !task.description?.trim()}
                  helperText={shouldShowError(`task_${selectedDayIndex}_${taskIndex}_description`) && !task.description?.trim() ? 'Task description is required' : ''}
                />
              </Box>
            ))}

            {/* Add more tasks button for selected day */}
            <Button
              variant="text"
              sx={{ color: '#FF6B35', textTransform: 'none', mb: 2 }}
              onClick={() => addTask(selectedDayIndex)}
              startIcon={<AddIcon />}
            >
              Add more tasks
            </Button>
          </Box>

          {/* Submit Button */}
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={handleSaveDraft}
              sx={{
                borderColor: '#FF6B35',
                color: '#FF6B35',
                py: 1.5,
                px: 4,
                borderRadius: '25px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
              }}
            >
              {isSavingDraft ? 'Saving Draft…' : 'Save as Draft'}
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitting}
              sx={{
                backgroundColor: '#FF6B35',
                py: 1.5,
                px: 6,
                borderRadius: '25px',
                textTransform: 'none',
                fontSize: '16px',
                fontWeight: 'bold',
                '&:hover': {
                  backgroundColor: '#E55A2B',
                },
              }}
            >
              {isSubmitting ? 'Creating Trip...' : 'Submit'}
            </Button>
          </Box>
        </Box>
      </Container>
  );
};

export default CreateTrip;